// shared/src/invariants/daily-close.ts
// Phase 8 Step 29 — DC-01 cross-component invariant per §4.13.2:7162.
// Phase 12.2.E — scope expanded from CANONICAL_TICKERS to the unified daily
// fetch universe (CANONICAL ∪ MACRO_PREDICTION_UNIVERSE_SEED). Extension
// coverage stays out of this static check (extensions are dynamic; their
// coverage is implicitly verified by the per-fetch FAIL check_log path).
//
// 2026-05-22 — INV_CRYPTO_PREFIX_CANONICAL added (P2 #11 audit). Validates
// that no bare `BTC`/`ETH` rows leak into daily_closes or signals after the
// Phase 12.2.E.pre `$`-prefix canonical convention lock. Adds a 7th entry
// to ALL_INVARIANTS — expands the §4.13.5 LOCK from 6 → 7 (Brief Revision
// surfaced to user). Asset-class predicate consolidated to
// `CRYPTO_TICKERS` Set (shared/src/tickers.ts) — no hand-rolled `BTC`/`ETH`
// string literals; this file's check uses that Set to derive the bare-form
// blocklist.

import { makeInvariant, type InvariantFn } from './types.js';
import { CRYPTO_TICKERS, MACRO_INDEX_TICKERS } from '../tickers.js';
import { loadWorkerParams, effectiveUniverse, type RuntimeParamsD1 } from '../runtime-params.js';
import { todayISODateInET, etPartsAt } from '../util/et-clock.js';

// Phase 6 §G (2026-05-25) — runtime-tunable universe from D1.
// Was a module-level constant computed from compile-time PARAMS + CANONICAL_TICKERS;
// now loads from D1 inside the invariant per docs/runtime-params-2026-05-25-brief-revision.md
// (operator can add a ticker without redeploying — propagation ≤60s).
//
// #50 (2026-07-27): loads via loadWorkerParams(['CONTEXT_TICKERS','TARGET_TICKERS']) — the
// exact subset effectiveUniverse() reads — NOT the full-strict loadRuntimeParams. This drops
// the last full-ParamsSchema dependency out of the invariant, so it (and its hosts
// daily-close-worker + api-worker /api/invariants) no longer break when an UNRELATED param is
// mid-retirement (a lingering/missing row for some other key can't trip .strict() here, because
// only these two keys are selected). Removes daily-close-worker from every retirement's blast
// radius; admin-params.ts is now the sole full-ParamsSchema consumer.
async function buildExpectedUniverse(db: RuntimeParamsD1): Promise<ReadonlyArray<string>> {
  // Session 5R 0027: MACRO_PREDICTION_UNIVERSE_SEED dropped. CONTEXT_TICKERS
  // is the single market universe (already a superset of the old seed) so the
  // union-with-seed disappears here without functional regression.
  // 2026-07-02 union: expected universe is CONTEXT ∪ TARGET (matches the
  // daily-close fetch list, which builds from effectiveUniverse). A TARGET
  // outside CONTEXT must be a daily_closes row too or DC-01 would flag drift.
  const params = await loadWorkerParams(db, ['CONTEXT_TICKERS', 'TARGET_TICKERS']);
  const set = new Set<string>(effectiveUniverse(params));
  return [...set].sort();
}

/**
 * DC-01-today-row-present — daily-close-worker coverage check.
 *
 * Cross-cut: daily-close-worker fires at 16:00 ET; expected to write one
 * `daily_closes` row per ticker in DAILY_CLOSE_EXPECTED_UNIVERSE. If ticker
 * registry drift OR per-ticker fetch failure occurs, some ticker is missing.
 *
 * Asserts: `SELECT DISTINCT ticker FROM daily_closes WHERE date = CURRENT_DATE`
 * ⊇ DAILY_CLOSE_EXPECTED_UNIVERSE (CANONICAL_TICKERS ∪ MACRO_PREDICTION_UNIVERSE_SEED).
 *
 * Severity policy:
 *   WARN — any expected ticker missing today (cross-component drift).
 *   PASS — every expected ticker has a row.
 *
 * NOTE: D1 `CURRENT_DATE` returns UTC date. Invariant fires from api-worker
 * Layer-2 route, so the comparison window depends on UTC clock vs the
 * 16:00 ET daily-close fire (20:00–21:00 UTC). A WARN result shortly before
 * the daily fire is expected and should not block cutover at v1 per the
 * §4.13.4 severity policy (only FAIL hard-blocks).
 */
// 2026-05-23 (post-audit B1) + 2026-07-17 (#70, ET-anchored) — the daily-close cron fires
// `30 20,21 * * 1-5` (20:30/21:30 UTC — a DST pair, each 30 min after the local 16:00 ET
// close). Before that window today's ET-labelled rows don't exist yet, and a WARN every
// minute adds noise (621 WARNs in the live check_log on the 2026-05-23 audit). Gate on ET
// wall-clock, NOT UTC: a fixed UTC 20:30 gate opened 30 min EARLY under EST (16:00 ET =
// 21:00 UTC there → a residual pre-close false-WARN edge). 16:30 ET covers both DST regimes.
// The daily_closes.date column is ET too (see DC-01), so weekday must be the ET weekday.
const DAILY_CLOSE_FIRST_FIRE_ET_MIN = 16 * 60 + 30;   // 16:30 ET — 30 min after the 16:00 ET fire

function dailyCloseExpectedYet(now: Date = new Date()): { expected: boolean; reason: string } {
  const et = etPartsAt(now.getTime());          // ET weekday + minutes since ET midnight
  if (et.weekday === 0 || et.weekday === 6) {
    return { expected: false, reason: 'weekend (ET) — no daily-close cron fires' };
  }
  if (et.minutes < DAILY_CLOSE_FIRST_FIRE_ET_MIN) {
    return { expected: false, reason: 'pre-close window — daily-close rows expected after 16:30 ET' };
  }
  return { expected: true, reason: 'post-close window — rows must be present' };
}

// Exported for unit-testing the time-gate predicate without binding to D1.
export const __dailyCloseExpectedYet = dailyCloseExpectedYet;

export const checkDailyCloseTodayRow: InvariantFn = makeInvariant(
  'DC-01-today-row-present',
  async (ctx) => {
    const gate = dailyCloseExpectedYet();
    const expected = await buildExpectedUniverse(ctx.db);
    // daily_closes.date is stamped ET wall-clock (daily-close-worker todayISODateInET),
    // so match it with an ET date — SQLite CURRENT_DATE is UTC and rolls to D+1 at 00:00Z
    // while the newest rows are still ET day D, which false-WARNed every ticker 00:00–04:00Z.
    const todayET = todayISODateInET();
    const rows = await ctx.db.prepare(
      `SELECT DISTINCT ticker FROM daily_closes WHERE date = ?`,
    ).bind(todayET).all<{ ticker: string }>();
    const todaySet = new Set((rows.results ?? []).map((r: { ticker: string }) => r.ticker));
    const missing  = expected.filter((t) => !todaySet.has(t));
    const total    = expected.length;
    const present  = total - missing.length;

    // Pre-close window: SKIP with an explanatory message regardless of row
    // presence (some rows may exist if a backfill was run, but coverage is
    // not contractually required yet).
    if (!gate.expected) {
      return {
        passed:   true,
        severity: 'SKIP',
        details:  `${gate.reason} — ${present}/${total} tickers populated so far`,
      };
    }
    const passed = missing.length === 0;
    return {
      passed,
      severity: passed ? 'PASS' : 'WARN',
      details: passed
        ? `all ${total} expected tickers have a daily_closes row for today (ET)`
        : `${missing.length}/${total} missing today (ET): ${missing.join(', ')}`,
    };
  },
);

/**
 * INV_CRYPTO_PREFIX_CANONICAL — D1 canonical-form discipline.
 *
 * Phase 12.2.E.pre lock: `$`-prefix is the canonical syntactic discriminator
 * for crypto tickers in D1. Migration `0004_phase-12.2-ticker-prefix.sql`
 * one-shot-migrated existing rows; this invariant catches any post-migration
 * regression that would re-introduce bare `BTC`/`ETH` rows (split-row JOIN
 * bug: `signals.ticker = daily_closes.ticker` silently misses bare-form rows
 * when canonical-form siblings exist).
 *
 * Asserts: zero rows in `daily_closes` OR `signals` whose ticker is a member
 * of CRYPTO_TICKERS Set without the `$`-prefix.
 *
 * Bare-form blocklist derived from shared/src/tickers.ts CRYPTO_TICKERS
 * (Set is bare-keyed by design — supports strip-then-lookup in
 * `isCryptoTicker`). No hand-rolled `BTC`/`ETH` literals here.
 *
 * Severity policy:
 *   PASS — zero bare crypto rows in either table.
 *   FAIL — any bare crypto row exists (real data-debt; loud break per
 *          feedback_no_tech_debt_pre_launch).
 */
const BARE_CRYPTO_TICKERS: ReadonlyArray<string> = Object.freeze([...CRYPTO_TICKERS]);

export const checkCryptoPrefixCanonical: InvariantFn = makeInvariant(
  'INV_CRYPTO_PREFIX_CANONICAL',
  async (ctx) => {
    // Two simple COUNT(*) queries — independent (no JOIN); each fits well
    // within D1's compound-SELECT cap (the D-33.D.10 lesson).
    const placeholders = BARE_CRYPTO_TICKERS.map(() => '?').join(',');
    const dailyRow = await ctx.db.prepare(
      `SELECT COUNT(*) AS n FROM daily_closes WHERE ticker IN (${placeholders})`,
    ).bind(...BARE_CRYPTO_TICKERS).first<{ n: number }>();
    const signalRow = await ctx.db.prepare(
      `SELECT COUNT(*) AS n FROM signals WHERE ticker IN (${placeholders})`,
    ).bind(...BARE_CRYPTO_TICKERS).first<{ n: number }>();
    // RC-A (audit 2026-05-29): macro_prediction_observables.indicator was the
    // table the bare-form rows actually accumulated in (the m-stage writer
    // persisted the raw LLM form). It was previously UNCHECKED here — the one
    // invariant meant to catch bare crypto was blind to the table that held
    // them. Column is `indicator` (not `ticker`).
    const obsRow = await ctx.db.prepare(
      `SELECT COUNT(*) AS n FROM macro_prediction_observables WHERE indicator IN (${placeholders})`,
    ).bind(...BARE_CRYPTO_TICKERS).first<{ n: number }>();

    const dailyBare  = dailyRow?.n  ?? 0;
    const signalBare = signalRow?.n ?? 0;
    const obsBare    = obsRow?.n    ?? 0;
    const total      = dailyBare + signalBare + obsBare;
    const passed     = total === 0;
    return {
      passed,
      severity: passed ? 'PASS' : 'FAIL',
      details: passed
        ? `zero bare-form crypto rows in daily_closes + signals + macro_prediction_observables (${BARE_CRYPTO_TICKERS.length} blocklist members checked)`
        : `bare-form crypto rows present: daily_closes=${dailyBare}, signals=${signalBare}, macro_prediction_observables=${obsBare} (canonical form is $-prefix per Phase 12.2.E.pre lock)`,
    };
  },
);

/**
 * DC-02-historical-coverage — daily_closes coverage over the prior 7
 * trading days.
 *
 * Cross-cut: DC-01 only checks today. Live D1 inspection on 2026-05-23
 * found 4/4/5 rows on 2026-05-18/19/20 vs the expected ~23 (signal that
 * the per-ticker fetcher mis-ran on those days). No invariant flagged
 * the gap because DC-01 only looks at CURRENT_DATE. This check catches
 * historical sparseness.
 *
 * Asserts: every UTC date in the last 7 calendar days that ALSO matches
 * a weekday has ≥ ceil(0.75 × universe_size) rows. Weekends excluded
 * (no cron). Today excluded (handled by DC-01 + the time gate).
 *
 * Severity:
 *   PASS — every prior weekday meets threshold.
 *   WARN — any prior weekday below threshold.
 */
// 2026-05-26 — US market holidays (NYSE closed). DC-02 doesn't expect
// daily_closes on these days. List is hand-curated for 2026; extend per
// year as needed. Source: NYSE official calendar.
//
// Limitation: this list only covers FULL closures. Early-close days
// (half-days around July 4 / Thanksgiving / Christmas) still get rows
// because daily-close fires post-close regardless of session length.
const US_MARKET_HOLIDAYS_2026: ReadonlySet<string> = new Set([
  '2026-01-01',  // New Year's Day
  '2026-01-19',  // MLK Day
  '2026-02-16',  // Presidents Day
  '2026-04-03',  // Good Friday
  '2026-05-25',  // Memorial Day
  '2026-06-19',  // Juneteenth
  '2026-07-03',  // Independence Day observed (July 4 = Saturday)
  '2026-09-07',  // Labor Day
  '2026-11-26',  // Thanksgiving
  '2026-12-25',  // Christmas
]);

export const checkDailyCloseHistoricalCoverage: InvariantFn = makeInvariant(
  'DC-02-historical-coverage',
  async (ctx) => {
    const expected    = await buildExpectedUniverse(ctx.db);
    const total       = expected.length;
    const minRequired = Math.ceil(total * 0.75);
    // 2026-05-27 — window narrowed from 7 → 5 calendar days. The 7-day
    // window kept flagging 2026-05-20=4 (a real outage during a hot
    // deploy on that date — too old to backfill cleanly; the row will
    // roll out of any reasonable window in ~2 days). 5-day window
    // still covers the last 3 weekdays in steady state (assuming no
    // weekend/holiday clustering), which is sufficient signal for
    // "did the daily-close ingest run for recent weekdays."
    // daily_closes.date is ET wall-clock — anchor the window to the ET "today" so the
    // [start, today) bounds don't slip a day at 00:00–04:00Z (UTC date('now') would be
    // D+1 while the newest ET-stamped rows are still day D). Same class as DC-01.
    const todayET     = todayISODateInET();
    const anchorMs    = Date.parse(`${todayET}T00:00:00Z`);
    const windowStart = new Date(anchorMs);
    windowStart.setUTCDate(windowStart.getUTCDate() - 5);
    const windowStartIso = windowStart.toISOString().slice(0, 10);
    const rows = await ctx.db.prepare(
      `SELECT date, COUNT(*) AS n
       FROM daily_closes
       WHERE date >= ?
         AND date < ?
         AND CAST(strftime('%w', date) AS INTEGER) BETWEEN 1 AND 5
       GROUP BY date
       ORDER BY date`,
    ).bind(windowStartIso, todayET).all<{ date: string; n: number }>();
    const dailyCounts = new Map<string, number>(
      (rows.results ?? []).map((r) => [r.date, r.n]),
    );

    // Enumerate expected weekdays in the window (yesterday-back 5 days).
    // Skip US market holidays — daily-close-worker doesn't expect rows
    // on NYSE-closed days (the cron still fires but vendors return null
    // / yesterday's close, so legitimate gaps look like coverage misses
    // without this filter). 2026-05-26 audit-r3 close fix.
    const expectedDates: string[] = [];
    for (let i = 1; i <= 5; i++) {
      const d = new Date(anchorMs);
      d.setUTCDate(d.getUTCDate() - i);
      const dow = d.getUTCDay();
      if (dow === 0 || dow === 6) continue;  // skip weekend
      const iso = d.toISOString().slice(0, 10);
      if (US_MARKET_HOLIDAYS_2026.has(iso)) continue;  // skip holiday
      expectedDates.push(iso);
    }

    const sparse: Array<{ date: string; n: number }> = [];
    for (const d of expectedDates) {
      const n = dailyCounts.get(d) ?? 0;
      if (n < minRequired) sparse.push({ date: d, n });
    }
    const passed = sparse.length === 0;
    return {
      passed,
      severity: passed ? 'PASS' : 'WARN',
      details: passed
        ? `all ${expectedDates.length} prior weekdays have ≥${minRequired}/${total} rows`
        : `${sparse.length} prior weekday(s) below threshold (${minRequired}/${total}): ` +
          sparse.map((s) => `${s.date}=${s.n}`).join(', '),
    };
  },
);

// #55 (2026-08-25) — the settled-close HEAL window. runCloseHeal backfills daily_closes holes for
// the last HEAL_WINDOW_DAYS SETTLED ET dates from oracle_canonical history; DC-03 below is the
// standing fence over that same window. In-code const (internal operational knob — no runtime_param,
// so no fixture-lockstep tax; feedback_internal_knob_escapes_fixture_lockstep) and the SINGLE source
// imported by daily-close-worker's heal path, so the sweep window and the fence window never drift.
export const HEAL_WINDOW_DAYS = 7;

/**
 * DC-03-settled-close-coverage — the strict, heal-aware, weekday-PRICE sibling of DC-02.
 *
 * DC-02 soft-WARNs at 75% over 5 weekdays and never distinguishes "healable-but-unhealed"; it did
 * not catch the 2026-08-18 / 08-23 whole-day holes loudly. DC-03 is the standing fence for the #55
 * heal: it reads the LIVE roster (effectiveUniverse — cannot drift against a hand-list) and the live
 * daily_closes, over the SAME [today-HEAL_WINDOW_DAYS, today) window the sweep covers.
 *
 * Roster = the PRICE roster (effectiveUniverse MINUS the 4 MACRO_INDEX_TICKERS), identical to
 * planHeal's healable set — so a v2-deferred macro-index hole can NEVER hold DC-03 in a permanent FAIL.
 *
 * Severity tiers (F2/F3):
 *   FAIL — a weekday, non-holiday settled date below the full price roster AND older than one heal
 *          cycle (≥2 days back — the daily sweep has had ≥1 chance to close it → real data-debt).
 *   WARN — (a) a breach only within the last heal cycle (yesterday; the sweep hasn't run yet), OR
 *          (b) any weekend/holiday price-roster gap (planHeal heals these best-effort, but the
 *          strict tier excludes them), OR (c) any of the 4 macro-index tickers missing (v2-deferred —
 *          a strict FAIL would be permanent until the macro-history heal ships).
 *   PASS — every settled date in the window has full price-roster coverage.
 *
 * Never enters today's ET date (the live-window date the sacred gate governs): the loop starts at
 * yesterday, and a defensive `date === todayET` skip guards a future off-by-one.
 */
export const checkSettledCloseCoverage: InvariantFn = makeInvariant(
  'DC-03-settled-close-coverage',
  async (ctx) => {
    const universe    = await buildExpectedUniverse(ctx.db);
    const priceRoster = universe.filter((t) => !MACRO_INDEX_TICKERS.has(t));
    const macroRoster = universe.filter((t) =>  MACRO_INDEX_TICKERS.has(t));
    const total       = priceRoster.length;

    const todayET  = todayISODateInET();
    const anchorMs = Date.parse(`${todayET}T00:00:00Z`);
    const windowStart = new Date(anchorMs);
    windowStart.setUTCDate(windowStart.getUTCDate() - HEAL_WINDOW_DAYS);
    const windowStartIso = windowStart.toISOString().slice(0, 10);

    // One read of the window; classify per-date in JS (window ≤7 days, roster small).
    const rows = await ctx.db.prepare(
      `SELECT date, ticker FROM daily_closes WHERE date >= ? AND date < ?`,
    ).bind(windowStartIso, todayET).all<{ date: string; ticker: string }>();
    const presentByDate = new Map<string, Set<string>>();
    for (const r of rows.results ?? []) {
      let set = presentByDate.get(r.date);
      if (!set) { set = new Set<string>(); presentByDate.set(r.date, set); }
      set.add(r.ticker);
    }

    const failDates:   Array<{ date: string; present: number }> = [];
    const warnReasons: string[] = [];
    for (let i = 1; i <= HEAL_WINDOW_DAYS; i++) {
      const d = new Date(anchorMs);
      d.setUTCDate(d.getUTCDate() - i);
      const date = d.toISOString().slice(0, 10);
      if (date === todayET) continue;   // structural guard — never the live-window date

      const present      = presentByDate.get(date) ?? new Set<string>();
      const missingPrice = priceRoster.filter((t) => !present.has(t));
      const missingMacro = macroRoster.filter((t) => !present.has(t));
      const dow          = new Date(`${date}T00:00:00Z`).getUTCDay();
      const isWeekend    = dow === 0 || dow === 6;
      const isHoliday    = US_MARKET_HOLIDAYS_2026.has(date);

      if (missingPrice.length > 0) {
        const presentCount = total - missingPrice.length;
        if (isWeekend || isHoliday) {
          warnReasons.push(`${date}=${presentCount}/${total} (weekend/holiday)`);
        } else if (i >= 2) {
          failDates.push({ date, present: presentCount });
        } else {
          warnReasons.push(`${date}=${presentCount}/${total} (within last heal cycle)`);
        }
      }
      if (missingMacro.length > 0) {
        warnReasons.push(`${date} macro-index missing: ${missingMacro.join(', ')} (v2)`);
      }
    }

    if (failDates.length > 0) {
      return {
        passed:   false,
        severity: 'FAIL',
        details:
          `${failDates.length} settled weekday date(s) below full price roster (${total}) older than one heal cycle — the heal should have closed these: ` +
          failDates.map((f) => `${f.date}=${f.present}/${total}`).join(', ') +
          (warnReasons.length ? `; also WARN: ${warnReasons.join('; ')}` : ''),
      };
    }
    if (warnReasons.length > 0) {
      return {
        passed:   false,
        severity: 'WARN',
        details: `settled-close coverage breaches (recent / weekend-holiday / macro-index v2): ${warnReasons.join('; ')}`,
      };
    }
    return {
      passed:   true,
      severity: 'PASS',
      details:  `all settled dates in the last ${HEAL_WINDOW_DAYS}d have full price-roster coverage (${total} tickers)`,
    };
  },
);

/** Catalog for §4.13.5 daily-close.ts lock (2026-05-22 expansion + 2026-05-23 historical-coverage add
 *  + 2026-08-25 #55 DC-03-settled-close-coverage). */
export const DAILY_CLOSE_INVARIANTS = [
  checkDailyCloseTodayRow,
  checkCryptoPrefixCanonical,
  checkDailyCloseHistoricalCoverage,
  checkSettledCloseCoverage,
] as const;
